/*
 *  Enums
 *
 * */
import {
  AccountingCustomerResponseBody,
  CashRegisterDocumentKindOfPaymentEnum,
  Decimal,
  MoneyResponseBody,
  PaymentInfoResponseBody,
  PriceResponseBody,
  RoundingDefinitionAndValidityResponseBody,
  SupplierResponseBody,
  TaxDocumentCalculationSummaryItemResponseBody,
  TaxDocumentCalculationV2Response,
  VatCheckTotalsDefinitionDocumentTypeEnum,
  VatType,
  FieldValue,
  CashRegisterDocumentTypeEnum,
  CreateCashRegisterDocumentItemRequestBody,
  UuidResponseBody,
  CashRegisterDocumentStateEnum,
  CashRegisterDocumentItemResponseBody,
  IssuerResponseBody,
  PaymentType,
  Alpha3,
  Money,
  PhoneNumber,
  CustomFieldValue,
} from '@dms/api/shared';

import {Nullish} from 'shared/web';

export type GetStockMovementsRequest = void;
export type GetStockMovementsResponse = {
  stockMovementType: string;
  name: string;
};

export type GetOperationalItemsRequest = void;
export type GetOperationalItemsResponse = {
  applicationId: string;
  itemCodeName: string;
  itemCode: string;
  operationId: string;
  itemGroup: string;
};

export type PayWithBankTransferRes = void;
export type PayWithBankTransferArg = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCardRes = void;
export type PayWithCardArg = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCardReceiptRes = void;
export type PayWithCardReceiptArg = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};
export type PayWithCashRes = void;
export type PayWithCashArg = {
  invoiceId: string;
  cashRegisterId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCashReceiptRes = void;
export type PayWithCashReceiptArg = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type InvoicingDocumentType =
  | 'invoice'
  | 'balance_invoice'
  | 'invoice_proforma'
  | 'tax_document_for_payment';
export type PaymentInfoRequestBody = {
  paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET' | 'FREE_OF_CHARGE';
  bankName?: string | null;
  bankAccount?: string | null;
  iban?: string | null;
  swift?: string | null;
  variableSymbol?: string | null;
  constantSymbol?: string | null;
  specificSymbol?: string | null;
};
export type CorrectiveTaxDocumentItemRequestBody = {
  name: string;
  quantity: Decimal;
  unit: string;
  pricePerUnit: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
};
export type CreateCorrectiveTaxDocumentRequestBody = {
  documentSeriesId: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentType;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentInfo: PaymentInfoRequestBody;
  additionalCustomer:
    | {
        customerId: string;
        contractInformationId: string;
        type: 'operator' | 'recipient';
      }
    | Nullish;
  items: InvoicingDocumentItemRequestBody[];
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
  customFields: CustomFieldValue[];
};
export type VatPriceResponseBody = {
  total: Money;
  base: Money;
  vatRate: string;
  vatType: VatType;
  vat: Money;
};
export type CorrectiveTaxDocumentItemResponseBody = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBody;
};
export type RenderedTemplateResponseBody = {
  url: string;
  pdfUrl: string;
};
export type RenderBalanceInvoiceDocumentRequestBody = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderCorrectiveTaxDocumentRequestBody = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderExpenseCashReceiptDocumentRequestBody = {
  cashRegisterDocumentId: string;
};
export type RenderIncomeCashReceiptDocumentRequestBody = {
  cashRegisterDocumentId: string;
};
export type RenderInvoiceDocumentRequestBody = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderInvoiceProformaDocumentRequestBody = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderTaxDocumentForPaymentDocumentRequestBody = {
  documentId: string;
  isMarginPrint?: boolean;
};

export type InvoicingDocumentTypeUnion =
  | 'invoice'
  | 'balance_invoice'
  | 'invoice_proforma'
  | 'tax_document_for_payment'
  | 'invoice_draft'
  | 'corrective_tax_document';
export type DocumentSeriesDefinitionResponseBody = {
  id: string;
  name: string;
  documentSeriesId: string;
  invoicingDocumentType: InvoicingDocumentTypeUnion;
  branchId: string | null;
  isSystem: boolean;
};
export type DocumentSeriesDefinitionRequestBody = {
  name: string;
  documentSeriesId: string;
  branchId: string | null;
  invoicingDocumentType: 'invoice' | 'corrective_tax_document';
};
export type InvoiceItemResponseBody = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBody;
  type: 'standard' | 'proforma_deduction';
};
export type InvoicePaymentResponseBody = {
  amount: Money;
  paymentId: string;
  transactionId: string;
  paymentType: PaymentType;
  source: 'internal' | 'external';
  paymentDate: string;
  cashRegisterDocumentId: string | null;
  cashRegisterDocumentNumber: string | null;
  cancelledTransactionId: string | null;
  cancelingTransactionId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentNumber: string | null;
};

export type AccountingCustomerRequestBody = {
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
};

export type SupplierDraftResponseBody = {
  id: string | null;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  fileNumber: string | null;
};
export type CustomerDraftResponseBody = {
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  printLanguage: string | null;
};
export type PaymentInfoDraftResponseBody = {
  paymentMethod: ('BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET' | 'FREE_OF_CHARGE') | null;
  bankAccount: string | null;
  iban: string | null;
  swift: string | null;
  variableSymbol: string | null;
  constantSymbol: string | null;
  specificSymbol: string | null;
};
export type InvoiceDraftResponseBody = {
  id: string;
  offerId: string | null;
  templateId: string | null;
  issueDate: string | null;
  dueDate: string | null;
  createdAt: string;
  author: string;
  dateOfTaxableSupply: string | null;
  dueSinceIssueDateInDays: number | null;
  branchId: string;
  currency: Alpha3 | null;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  additionalCustomer: {
    customerId: string;
    contractInformationId: string;
    type: 'operator' | 'recipient';
    name: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    country: string;
    cin: string;
    tin: string;
    phoneNumber: {
      countryCode: string;
      prefix: string;
      number: string;
    };
  } | null;
  supplier: SupplierDraftResponseBody;
  customer: CustomerDraftResponseBody;
  paymentInfo: PaymentInfoDraftResponseBody;
  type: 'DRAFT';
  state: 'IN_PROGRESS';
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};
export type InvoiceDraftItemRequestBody = {
  name?: string;
  quantity?: Decimal;
  unit?: string;
  pricePerUnit?: string;
  isUnitPriceWithVat?: boolean;
  vatType?: VatType;
  currency?: Alpha3;
};
export type InvoiceDraftRequestBody = {
  offerId?: string;
  templateId?: string;
  issueDate?: string;
  dueDate?: string;
  dateOfTaxableSupply?: string;
  currency?: Alpha3;
  note?: string | null;
  footerNote?: string | null;
  internalNote?: string | null;
  invoiceItems?: InvoiceDraftItemRequestBody[];
  billingInformationId: string;
  branchId: string;
  customer?: AccountingCustomerRequestBody;
  additionalCustomer:
    | {
        customerId: string;
        contractInformationId: string;
        type: 'operator' | 'recipient';
      }
    | Nullish;
  paymentInfo?: {
    paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET';
    bankName?: string | null;
    bankAccount?: string | null;
    iban?: string | null;
    swift?: string | null;
    variableSymbol?: string | null;
    constantSymbol?: string | null;
    specificSymbol?: string | null;
  };
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type MoneyDraftResponseBody = {
  amount?: Decimal;
  currency?: Alpha3;
};
export type InvoiceDraftItemResponseBody = {
  name: string | null;
  quantity: Decimal | null;
  unit: string | null;
  pricePerUnit: MoneyDraftResponseBody | null;
  isUnitPriceWithVat: boolean | null;
  vatType: string | null;
  currency: string | null;
};
export type PaymentStateType =
  | 'draft'
  | 'canceled'
  | 'completed'
  | 'confirmed'
  | 'partially_paid'
  | 'overpayment'
  | 'reopened'
  | 'canceling'
  | 'overdue';

export type InvoiceProformaResponseBody = {
  id: string;
  offerId: string | null;
  templateId: string;
  number: string;
  type: 'ISSUED' | 'ACCEPTED' | 'PROMO';
  paymentState: PaymentStateType;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  additionalCustomer: {
    customerId: string;
    contractInformationId: string;
    type: 'operator' | 'recipient';
    name: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    country: string;
    cin: string;
    tin: string;
    phoneNumber: {
      countryCode: string;
      prefix: string;
      number: string;
    };
  } | null;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  cancelledDocumentId: string | null;
  supplier: SupplierResponseBody;
  customer: AccountingCustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  invoiceSummary: TaxDocumentCalculationResponseBody;
  exchangeRateRatioInvoiceSummary: TaxDocumentCalculationResponseBody;
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
  customFieldsPayload: CustomFieldValue[];
};

export type InvoiceProformaItemResponseBody = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBody;
};
export type InvoiceProformaPaymentResponseBody = {
  amount: Money;
  paymentId: string;
  transactionId: string;
  paymentType: PaymentType;
  paymentDate: string;
  source: 'internal' | 'external';
  cashRegisterDocumentId: string | null;
  cashRegisterDocumentNumber: string | null;
  taxDocumentForPaymentId: string | null;
  taxDocumentForPaymentNumber: string | null;
  cancelledTransactionId: string | null;
  cancelingTransactionId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentNumber: string | null;
};

export type GetCorrectiveTaxDocumentItemsApiResponse = CorrectiveTaxDocumentItemResponseBody[];
export type GetCorrectiveTaxDocumentItemsApiArg = {
  correctiveTaxDocumentId: string;
};
export type SendCorrectiveTaxDocumentApiResponse = unknown;
export type SendCorrectiveTaxDocumentApiArg = {
  correctiveTaxDocumentId: string;
};
export type RenderBalanceInvoiceDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderBalanceInvoiceDocumentApiArg = {
  renderBalanceInvoiceDocumentRequestBody: RenderBalanceInvoiceDocumentRequestBody;
};
export type RenderCorrectiveTaxDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderCorrectiveTaxDocumentApiArg = {
  renderCorrectiveTaxDocumentRequestBody: RenderCorrectiveTaxDocumentRequestBody;
};
export type RenderExpenseCashReceiptDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderExpenseCashReceiptDocumentApiArg = {
  renderExpenseCashReceiptDocumentRequestBody: RenderExpenseCashReceiptDocumentRequestBody;
};
export type RenderIncomeCashReceiptDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderIncomeCashReceiptDocumentApiArg = {
  renderIncomeCashReceiptDocumentRequestBody: RenderIncomeCashReceiptDocumentRequestBody;
};
export type RenderInvoiceDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderInvoiceDocumentApiArg = {
  renderInvoiceDocumentRequestBody: RenderInvoiceDocumentRequestBody;
};
export type RenderInvoiceProformaDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderInvoiceProformaDocumentApiArg = {
  renderInvoiceProformaDocumentRequestBody: RenderInvoiceProformaDocumentRequestBody;
};
export type RenderTaxDocumentForPaymentDocumentApiResponse = RenderedTemplateResponseBody;
export type RenderTaxDocumentForPaymentDocumentApiArg = {
  renderTaxDocumentForPaymentDocumentRequestBody: RenderTaxDocumentForPaymentDocumentRequestBody;
};
export type CancelInvoiceApiResponse = /** status 204  */ undefined;
export type CancelInvoiceApiArg = {
  invoiceId: string;
};
export type CreateDocumentSeriesDefinitionApiResponse = DocumentSeriesDefinitionResponseBody;
export type CreateDocumentSeriesDefinitionApiArg = {
  documentSeriesDefinitionRequestBody: DocumentSeriesDefinitionRequestBody;
};
export type GetDocumentSeriesDefinitionListApiResponse = DocumentSeriesDefinitionResponseBody[];
export type GetDocumentSeriesDefinitionListApiArg = {};

export type ReprocessErrorLogApiArg = {
  documentId: string;
  errorLogId: string;
};

export type ReprocessErrorLogApiRes = void;
export type GetErrorLogApiRes = {
  id: string | Nullish;
  createdAt: string | Nullish;
  documentId: string | Nullish;
  documentType: string | Nullish;
  interface: string | Nullish;
  operation: string | Nullish;
  errorMessage: string | Nullish;
  notes: string | Nullish;
};
export type GetErrorLogApiArg = {id: string};

export type DeleteDocumentSeriesDefinitionApiResponse = /** status 204  */ undefined;
export type DeleteDocumentSeriesDefinitionApiArg = {
  documentSeriesDefinitionId: string;
};
export type UpdateDocumentSeriesDefinitionApiResponse = DocumentSeriesDefinitionResponseBody;
export type UpdateDocumentSeriesDefinitionApiArg = {
  documentSeriesDefinitionId: string;

  documentSeriesDefinitionRequestBody: DocumentSeriesDefinitionRequestBody;
};
export type TaxDocumentCalculationItemRequestBody = {
  quantity: string;
  reference: string;
  unitPrice: string;
  relatedItemId?: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
};
export type GetCategoryApiArg = {
  category: 'matrices-config-from-metada-to-google' | 'matrices-config-from-google-to-metada';
};
export type GetCategoryApiRes = {
  category: string | null;
  code: string | null;
  name: string | null;
  priority: string | null;
}[];
export type AccPostConfigurationPaymentImportModeApiResponse =
  /** status 204 Standard response */ undefined;

export type PaymentImportMode =
  | 'importTrxOnly'
  | 'importTrxDmsCreateTaxDocument'
  | 'importTaxDocumentOnly';
export type AccPostConfigurationPaymentImportModeApiArg = {
  body: {
    branchId: string;
    paymentImportMode: PaymentImportMode;
  } | null;
};
export type AccPatchConfigurationPaymentImportModeApiResponse =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationPaymentImportModeApiArg = {
  branchId: string | null;
  body: {
    paymentImportMode: PaymentImportMode;
  } | null;
};

export type AccGetConfigurationPaymentImportModeApiResponse =
  | /** status 200 Standard response */ ({
      branchId?: string | null;
      paymentImportMode?: PaymentImportMode | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationPaymentImportModeApiArg = {
  branchId: string;
};
export type AccDeleteConfigurationPaymentImportModeApiResponse =
  /** status 204 Standard response */ undefined;
export type AccDeleteConfigurationPaymentImportModeApiArg = {
  branchId: string;
};
export type MoveMatricesApiArg = {
  category: 'matrices-config-from-metada-to-google' | 'matrices-config-from-google-to-metada';
  tables: string[];
};
export type MoveAllMatricesFromCarisApiArg = {
  googleDocId: string;
  sheetName: string;
  validFrom: string;
  validTo: string;
};
export type MoveAllMatricesFromCarisApiRes = unknown;
export type MoveMatricesApiRes = unknown;

export type MatricesConfigCleanupApiArg = {
  tables: string[];
};
export type MatricesConfigCleanupApiRes = unknown;

export type AccountingDocumentRecalculateApiArg = {
  country: string;
  currency: string;
  dueDate: string;
  items: {
    itemId: string;
    unit: string;
    relatedItemId: string | null;
    quantity: string;
    unitPrice: string;
    unitType: 'standard' | 'percent';
    isUnitPriceWithVat: boolean;
    vatType: VatType;
    type: InvoicingDocumentAccountingItemType;
    description: string;
    currency: string;
  }[];
  branchId: string | null;
  paymentType: PaymentType;
  vatCheckTotalsDocumentType: VatCheckTotalsDefinitionDocumentTypeEnum;
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type AccountingDocumentRecalculateItemRes = {
  itemId: string;
  relatedItemId: string | null;
  quantity: string;
  unitPrice: string;
  unit: string;
  unitType: 'standard' | 'percent';
  priceWithVat: string;
  priceWithoutVat: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  type: InvoicingDocumentAccountingItemType;
  description: string;
  currency: string;
  discount: InvoicingDocumentItemDiscountRequestBody;
};
export type AccountingDocumentRecalculateApiRes = {
  items: AccountingDocumentRecalculateItemRes[];
  documentSummary: {
    calculation: TaxDocumentCalculationV2Response;
    exchangeRateRatioCalculation: TaxDocumentCalculationV2Response;
  };
};

export type TaxDocumentForPaymentItemResponseBody = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBody;
};
export type TaxDocumentForPaymentResponseBody = {
  id: string;
  invoiceProformaId: string;
  userId: string;
  templateId: string;
  number: string;
  createdAt: string;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  internalNote: string | null;
  documentItems: TaxDocumentForPaymentItemResponseBody[];
  supplier: SupplierResponseBody;
  customer: AccountingCustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  taxDocumentSummary: TaxDocumentCalculationResponseBody;
  exchangeRateRatioInvoiceSummary: TaxDocumentCalculationResponseBody;
  customFieldsPayload: CustomFieldValue[];
  paymentState: never;
  cancellable: boolean;
  source: 'internal' | 'external';
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type TaxDocumentCalculationRequestBody = {
  country: string;
  currency: string;
  taxableSupplyDate: string;
  items: TaxDocumentCalculationItemRequestBody[];
  branchId: string | null;
  paymentType: PaymentType;
  vatCheckTotalsDocumentType: VatCheckTotalsDefinitionDocumentTypeEnum;
  exchangeRateRatio?:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type ChangeInvoiceCustomerApiArg = {
  invoiceId: string;
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
};

export type GetInvoiceItemsApiResponse = InvoiceItemResponseBody[];
export type GetInvoiceItemsApiArg = {
  invoiceId: string;
};
export type GetInvoicePaymentListApiResponse = InvoicePaymentResponseBody[];
export type GetInvoicePaymentListApiArg = {
  invoiceId: string;
};

export type GetCorrectiveTaxDocumentPaymentListApiArg = {
  correctiveTaxDocumentId: string;
};
export type GetCorrectiveTaxDocumentPaymentListApiResponse = InvoicePaymentResponseBody[];

export type SendInvoiceApiResponse = /** status 204  */ undefined;
export type SendInvoiceApiArg = {
  invoiceId: string;
};
export type DeleteInvoiceDraftApiResponse = /** status 204  */ undefined;
export type DeleteInvoiceDraftApiArg = {
  invoiceDraftId: string;
};

export type PutInvoiceDraftApiResponse = InvoiceDraftResponseBody;
export type PutInvoiceDraftApiArg = {
  invoiceDraftId: string;

  invoiceDraftRequestBody: InvoiceDraftRequestBody;
};

export type InvoiceTypes =
  | 'DRAFT'
  | 'ISSUED'
  | 'CORRECTIVE_TAX_DOCUMENT'
  | 'BALANCE_INVOICE'
  | 'TAX_DOCUMENT_FOR_PAYMENT'
  | 'PROFORMA';

export type GetInvoiceDraftItemsApiResponse = InvoiceDraftItemResponseBody[];
export type GetInvoiceDraftItemsApiArg = {
  invoiceDraftId: string;
};
export type PostInvoiceDraftApiResponse = InvoiceDraftResponseBody;
export type PostInvoiceDraftApiArg = {
  invoiceDraftRequestBody: InvoiceDraftRequestBody;
};
export type CancelInvoiceProformaApiArg = {
  invoiceProformaId: string;
};
export type CancelInvoiceProformaApiResponse = unknown;

export type GetInvoiceProformaItemsApiResponse = InvoiceProformaItemResponseBody[];
export type GetInvoiceProformaItemsApiArg = {
  invoiceId: string;
};
export type GetInvoiceProformaPaymentListApiResponse = InvoiceProformaPaymentResponseBody[];
export type GetInvoiceProformaPaymentListApiArg = {
  invoiceId: string;
};
export type PaymentTypeResponseBody = {
  key: string;
  value: string;
};
export type GetPaymentTypesForInvoicePaymentApiResponse = PaymentTypeResponseBody[];
export type GetPaymentTypesForInvoicePaymentApiArg = Record<string, never>;

export type GetPaymentTypeEnumApiResponse = PaymentTypeResponseBody[];

export type TaxDocumentCalculationApiArg = {
  taxDocumentCalculationRequestBody: TaxDocumentCalculationRequestBody;
};

export type GetBalanceInvoiceByInvoiceProformaIdApiResponse = BalanceInvoiceV2ResponseBody;
export type GetBalanceInvoiceByInvoiceProformaIdApiArg = {
  invoiceProformaId: string;
};
export type GetBalanceInvoiceApiResponse = BalanceInvoiceV2ResponseBody;
export type GetBalanceInvoiceApiArg = {
  balanceInvoiceId: string;
};
export type GetCorrectiveTaxDocumentApiResponse = CorrectiveTaxDocumentV3ResponseBody;

export type CheckoutCorrectiveTaxDocumentType = 'partial' | 'full';
export type GetCorrectiveTaxDocumentApiArg = {
  correctiveTaxDocumentId: string;
};
export type GetInvoiceApiResponse = InvoiceV3ResponseBody;
export type GetInvoiceApiArg = {
  invoiceId: string;
};
export type CreateInvoiceDraftApiResponse = {
  id: string;
};
export type CreateInvoiceDraftApiArg = {
  invoiceDraftV2RequestBody: InvoiceDraftV2RequestBody;
};
export type GetInvoiceDraftApiResponse = InvoiceDraftV2ResponseBody;
export type GetInvoiceDraftApiArg = {
  invoiceDraftId: string;
};
export type UpdateInvoiceDraftApiResponse = unknown;
export type UpdateInvoiceDraftApiArg = {
  id: string;

  invoiceDraftV2RequestBody: InvoiceDraftV2RequestBody;
};
export type CreateInvoiceProformaApiResponse = {
  id: string;
};
export type CreateInvoiceProformaApiArg = {
  invoiceProformaV2RequestBody: InvoiceProformaV2RequestBody;
};
export type GetInvoiceProformaApiResponse = InvoiceProformaV2ResponseBody;
export type GetInvoiceProformaApiArg = {
  invoiceProformaId: string;
};
export type PayWithBankTransferApiResponse = unknown;
export type PayWithBankTransferApiArg = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCardAndCreateReceiptApiArg = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  amount: Money;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?: ExchangeRateRatioBody;
};
export type PayWithCardApiArg = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCashAndCreateReceiptApiArg = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  amount: Money;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?: ExchangeRateRatioBody;
};

export type CreateTaxDocumentSettingApiResponse = TaxDocumentSettingResponseBody;
export type CreateTaxDocumentSettingApiArg = {
  taxDocumentSettingRequestBody: TaxDocumentSettingRequestBody;
};
export type DeleteTaxDocumentSettingApiResponse = unknown;
export type DeleteTaxDocumentSettingApiArg = {
  id: string;
};
export type GetTaxDocumentSettingApiResponse = TaxDocumentSettingResponseBody;
export type GetTaxDocumentSettingListApiResponse = TaxDocumentSettingResponseBody[];
export type GetTaxDocumentSettingListApiArg = void;
export type GetTaxDocumentSettingApiArg = {
  id: string;
};
export type UpdateTaxDocumentSettingApiResponse = TaxDocumentSettingResponseBody;
export type UpdateTaxDocumentSettingApiArg = {
  id: string;

  updateTaxDocumentSettingRequestBody: UpdateTaxDocumentSettingRequestBody;
};
export type VatCalculationType = 'from-above' | 'from-below' | 'sum';
export type VatCheckTotalsDocumentTypeV2 =
  | 'cash-receipts'
  | 'invoicing-documents'
  | 'invoices'
  | 'off';
export type CreateVatTotalAndCalculationSettingRequestBody = {
  branchId: string;
  calculationType: VatCalculationType;
  documentType: VatCheckTotalsDocumentTypeV2 | null;
};
export type VatTotalAndCalculationSettingResponseBody = {
  id: string;
  branchId: string | null;
  calculationType: VatCalculationType;
  documentType: VatCheckTotalsDocumentTypeV2 | null;
  isSystem: boolean;
};
export type UpdateVatTotalAndCalculationSettingRequestBody = {
  calculationType: VatCalculationType;
  documentType: VatCheckTotalsDocumentTypeV2 | null;
  vatTotalAndCalculationId: string;
};
export type CreateVatTotalAndCalculationSettingApiResponse = object;
export type CreateVatTotalAndCalculationSettingApiArg =
  CreateVatTotalAndCalculationSettingRequestBody;

export type DeleteVatTotalAndCalculationSettingApiResponse = unknown;
export type DeleteVatTotalAndCalculationSettingApiArg = {
  vatTotalAndCalculationId: string;
};
export type GetVatTotalAndCalculationSettingApiResponse = VatTotalAndCalculationSettingResponseBody;
export type GetVatTotalAndCalculationSettingApiArg = {
  vatTotalAndCalculationId: string;
};
export type UpdateVatTotalAndCalculationSettingApiResponse =
  VatTotalAndCalculationSettingResponseBody;
export type UpdateVatTotalAndCalculationSettingApiArg =
  UpdateVatTotalAndCalculationSettingRequestBody;
export type GetListVatTotalAndCalculationSettingApiResponse =
  VatTotalAndCalculationSettingResponseBody[];
export type GetListVatTotalAndCalculationSettingApiArg = void;
export type IssuedType = 'automatic' | 'manual' | 'not_create';
export type TaxDocumentSettingResponseBody = {
  id: string;
  branchId: string | null;
  issuedType: IssuedType;
  isSystem: boolean;
};
export type TaxDocumentSettingRequestBody = {
  branchId: string;
  issuedType: IssuedType;
};
export type UpdateTaxDocumentSettingRequestBody = {
  branchId: string | null;
  issuedType: IssuedType;
};

export type PayWithCashApiResponse = unknown;
export type PayWithCashApiArg = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  cashRegisterId: string | null;
  amount: Money;
};

export type PayWithCardAndCreateReceiptApiResponse = unknown;
export type PayWithCardApiResponse = unknown;
export type PayWithCashAndCreateReceiptApiResponse = unknown;

export type CreateDiscountItemApiResponse = InvoicingDocumentItemRequestBody;
export type CreateDiscountItemApiArg = {
  createInvoicingItemDiscountRequestBody: CreateInvoicingItemDiscountRequestBody;
};
export type CreateMarginItemApiResponse = InvoicingDocumentItemRequestBody;
export type CreateMarginItemApiArg = {
  createInvoicingItemMarginRequestBody: CreateInvoicingItemMarginRequestBody;
};
export type CreateStandardItemApiResponse = InvoicingDocumentItemRequestBody;
export type CreateStandardItemApiArg = {
  createInvoicingItemStandardRequestBody: CreateInvoicingItemStandardRequestBody;
};
export type CreateStandardMultipleItemsApiResponse = InvoicingDocumentItemRequestBody[];
export type CreateStandardMultipleItemsApiArg = {
  createInvoicingItemsStandardRequestBodies: CreateInvoicingItemsStandardRequestBodies;
};

export type GetCalculatePercentageApiRes = {
  discountValue: string;
  originalValue: string;
  percent: string;
};
export type GetCalculatePercentageApiArg = {
  discountValue: string;
  originalValue: string;
};

export type GetCalculateByPercentageApiRes = {
  discountValue: string;
  originalValue: string;
  percent: string;
};
export type GetCalculateByPercentageApiArg = {
  percent: string;
  originalValue: string;
};

export type InvoicingDocumentAccountingItemType = 'standard' | 'margin' | 'discount';
export type GeneratedItemType = 'standard' | 'proforma_deduction';
export type InvoicingDocumentItemDiscountRequestBody = {
  relatedItemId: string;
  percent: string;
  originalPrice: string;
  priceAfterDiscount: string;
  discountValue: string;
  currency: Alpha3;
};
export type InvoicingDocumentItemResponseBody = {
  itemId: string;
  description: string;
  quantity: string;
  unit: string;
  pricePerUnit: Money;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
  totalPrice: VatPriceResponseBody;
  type: InvoicingDocumentAccountingItemType;
  generatedItemType: GeneratedItemType;
  relatedItemId: string | null;
  discount: InvoicingDocumentItemDiscountRequestBody | null;
};
export type PaymentStateResponseBody =
  | 'draft'
  | 'canceled'
  | 'completed'
  | 'confirmed'
  | 'partially_paid'
  | 'overpayment'
  | 'reopened'
  | 'overdue';

type CustomerResponseBody = {
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  printLanguage: string | null;
};

export type TaxDocumentForCalculationItemType = 'standard' | 'additionalCalculation';
type TaxDocumentCalculationItemResponseBody = {
  reference: string;
  relatedItemId?: string;
  quantity: string;
  vatType: VatType;
  unitPrice: Money;
  isUnitPriceWithVat: boolean;
  totalPriceWithoutVat: Money;
  totalPriceWithVat: Money;
  type: TaxDocumentForCalculationItemType;
};
export type TaxDocumentForCalculationSummaryItemType =
  | 'documentPriceWithVatRounding'
  | 'documentPriceWithVatRoundingVat'
  | 'documentPriceWithVatRoundingWithoutVat';

export type RoundType = 'UP' | 'DOWN' | 'ALGEBRAIC';

type TaxDocumentCalculationResponseBody = {
  taxableSupplyDate: string;
  vatTypes: VatType[];
  vatTypesSummary: {
    [key: string]: {
      groupSummary: PriceResponseBody;
      additionalCalculation: PriceResponseBody | null;
      vatRate: string;
    };
  };
  priceWithVat: Money;
  priceWithoutVat: Money;
  priceWithVatBeforeRounding: Money;
  vat: Money;
  items: TaxDocumentCalculationItemResponseBody[];
  summaryItems: TaxDocumentCalculationSummaryItemResponseBody[];
  usedRoundingDefinitionAndValidity: RoundingDefinitionAndValidityResponseBody | null;
};

export type AdditionalCustomerResponseBody = {
  customerId: string | null;
  contractInformationId: string;
  type: 'operator' | 'recipient';
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  phoneNumber: PhoneNumber | null;
} | null;
export type ExchangeRateRatioBody = {
  amount: number;
  ratio: string;
  currency: Alpha3;
} | null;
export type InvoiceV3ResponseBody = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  paymentState: PaymentStateResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  createdById: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: Money;
  totalPriceWithoutVat: Money;
  documentSummary: TaxDocumentCalculationResponseBody;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
  invoiceCategory: 'default' | 'internal' | null;
};
export type BalanceInvoiceV2ResponseBody = {
  invoice: InvoiceV3ResponseBody;
  proformaIds: string[];
};

export type InvoicingDocumentItemRequestBody = {
  itemId: string;
  description: string;
  quantity: string;
  unit: string;
  pricePerUnit: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
  type: InvoicingDocumentAccountingItemType;
  relatedItemId?: string | null;
  discount?: InvoicingDocumentItemDiscountRequestBody | null;
  correctedItemId?: string | null;
};
export type AdditionalCustomerRequestBody = {
  customerId: string;
  contractInformationId: string;
  type: 'operator' | 'recipient';
} | null;
export type CorrectiveTaxDocumentV3ResponseBody = {
  id: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentType;
  invoicingDocumentNumber: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  dueSinceIssueDateInDays: number;
  paymentState: PaymentStateResponseBody;
  author: string;
  createdAt: string;
  customer: CustomerResponseBody;
  totalPriceWithVat: Money;
  totalPriceWithoutVat: Money;
  paymentInfo: PaymentInfoResponseBody;
  isFull: boolean;
  supplier: SupplierResponseBody;
  currency: Alpha3;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentPrescriptionId: string;
  branchId: string;
  documentSummary: TaxDocumentCalculationResponseBody;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
  invoiceCategory: 'default' | 'internal' | null;
};
type CustomerRequestBody = {
  id: string | null;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
};
export type InvoiceDraftItemV2RequestBody = {
  itemId: string;
  description?: string | null;
  quantity?: string | null;
  unit?: string | null;
  pricePerUnit?: string | null;
  isUnitPriceWithVat?: boolean | null;
  vatType?: VatType | null;
  currency?: Alpha3 | null;
  type: InvoicingDocumentAccountingItemType;
  relatedItemId?: string | null;
  discount?: InvoicingDocumentItemDiscountRequestBody | null;
};
export type InvoiceDraftV2RequestBody = {
  branchId: string;
  issueDate?: string | null;
  dueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency?: Alpha3 | null;
  note?: string | null;
  footerNote?: string | null;
  internalNote?: string | null;
  items: InvoiceDraftItemV2RequestBody[];
  billingInformationId: string;
  customer?: CustomerRequestBody | null;
  paymentInfo?: PaymentInfoRequestBody | null;
  additionalCustomer?: AdditionalCustomerRequestBody | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBody | null;
};

export type InvoiceDraftItemV2ResponseBody = {
  itemId: string;
  description: string | null;
  quantity: string | null;
  unit: string | null;
  pricePerUnit: MoneyDraftResponseBody | null;
  isUnitPriceWithVat: boolean | null;
  vatType: VatType | null;
  currency: Alpha3 | null;
  type: InvoicingDocumentAccountingItemType;
  relatedItemId: string | null;
  discount: InvoicingDocumentItemDiscountRequestBody | null;
};

export type InvoiceDraftV2ResponseBody = {
  id: string;
  items: InvoiceDraftItemV2ResponseBody[];
  dueSinceIssueDateInDays: number;
  branchId: string;
  dateOfTaxableSupply: string | null;
  currency: Alpha3 | null;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string | null;
  issueDate: string | null;
  author: string;
  createdById: string;
  createdAt: string;
  supplier: SupplierDraftResponseBody | null;
  customer: CustomerDraftResponseBody | null;
  paymentInfo: PaymentInfoDraftResponseBody | null;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: FieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
};
export type InvoiceProformaV2RequestBody = {
  items: InvoicingDocumentItemRequestBody[];
  documentSeriesId: string;
  footerNote?: string | null;
  internalNote?: string | null;
  paid?: boolean;
  markAsSent?: boolean;
  branchId: string;
  issueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency: Alpha3;
  note?: string | null;
  billingInformationId: string;
  customer: CustomerRequestBody;
  dueDate: string;
  paymentInfo: PaymentInfoRequestBody;
  additionalCustomer?: AdditionalCustomerRequestBody | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBody | null;
};
export type InvoiceProformaV2ResponseBody = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  paymentState: PaymentStateResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdById: string;
  createdAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: Money;
  totalPriceWithoutVat: Money;
  documentSummary: TaxDocumentCalculationResponseBody;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
};
export type CreateInvoicingItemDiscountDefinitionRequestBody = {
  percent: string;
  description: string;
};
export type CreateInvoicingItemDiscountRequestBody = {
  originalItem: InvoicingDocumentItemRequestBody;
  discount: CreateInvoicingItemDiscountDefinitionRequestBody;
};
export type CreateInvoicingItemMarginDefinitionRequestBody = {
  description: string;
  price: string;
};
export type CreateInvoicingItemMarginRequestBody = {
  originalItem: InvoicingDocumentItemRequestBody;
  margin: CreateInvoicingItemMarginDefinitionRequestBody;
};
export type CreateInvoicingItemStandardRequestBody = {
  description: string;
  quantity: string;
  unit: string;
  pricePerUnit: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
};
export type CreateInvoicingItemsStandardRequestBodies = {
  invoicingItems: CreateInvoicingItemStandardRequestBody[];
};
export type AccPostConfigurationInvoiceCostItemApiResponse =
  | /** status 200 Standard response */ ({
      id?: string | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccPostConfigurationInvoiceCostItemApiArg = {
  branchId: string | null | undefined;
  generateCostItem: boolean | null;
};
export type AccGetConfigurationInvoiceCostItemApiResponse =
  | /** status 200 Standard response */ ({
      branchId?: string | null | undefined;
      generateCostItem?: boolean | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationInvoiceCostItemApiArg = {
  id: string;
};
export type AccPatchConfigurationInvoiceCostItemApiResponse =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationInvoiceCostItemApiArg = {
  id: string | null;
  branchId?: string | null | undefined;
  generateCostItem: boolean | null;
};
export type AccDeleteConfigurationApiResponse = /** status 204 Standard response */ undefined;
export type AccDeleteConfigurationApiArg = {
  id: string;
};

export type AccPostMatrixConfigInitializeApiResponse = Record<string, unknown> | undefined;

export type AccPostMatrixConfigInitializeApiArg = void;

export type AccGetConfigurationExportCarisApiResponse =
  | /** status 200 Standard response */ ({
      exportNewOnly?: ({
        id?: string | null;
        filterValue?: string | null;
        parameterValue?: boolean | null;
      } | null)[];
      exportDateOffset?: ({
        id?: string | null;
        filterValue?: string | null;
        parameterValue?: number | null;
      } | null)[];
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationExportCarisApiArg = void;
export type AccPatchConfigurationExportCarisApiResponse =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationExportCarisApiArg = {
  exportNewOnly?: ({
    id: string;
    parameterValue: boolean | null;
  } | null)[];
  exportDateOffset?: ({
    id: string;
    parameterValue: number | null;
  } | null)[];
} | null;

export type AccPostDeleteErrorLogsApiArg = {
  id: number[];
  deleteAll?: boolean;
};

export type AccPostDeleteErrorLogsApiResponse = void;

export type CreatePreAccountingProcessingSettingsApiResponse =
  PreAccountingProcessingSettingsResponseBody;
export type CreatePreAccountingProcessingSettingsApiArg = {
  createPreAccountingProcessingSettingsRequestBody: CreatePreAccountingProcessingSettingsRequestBody;
};
export type DeletePreAccountingProcessingSettingsApiResponse = unknown;
export type DeletePreAccountingProcessingSettingsApiArg = {
  id: string;
};
export type GetPreAccountingProcessingSettingsApiResponse =
  PreAccountingProcessingSettingsResponseBody;
export type GetPreAccountingProcessingSettingsApiArg = {
  id: string;
};
export type UpdatePreAccountingProcessingSettingsApiResponse =
  PreAccountingProcessingSettingsResponseBody;
export type UpdatePreAccountingProcessingSettingsApiArg = {
  id: string;
  updatePreAccountingProcessingSettingsRequestBody: UpdatePreAccountingProcessingSettingsRequestBody;
};
export type GetListPreAccountingProcessingSettingsApiResponse =
  PreAccountingProcessingSettingsResponseBody[];
export type GetListPreAccountingProcessingSettingsApiArg = void;
export type PreAccountingProcessingSettingsResponseBody = {
  id: string;
  branchId: string | null;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
  isSystem: boolean;
};
export type CreatePreAccountingProcessingSettingsRequestBody = {
  branchId: string;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
};
export type UpdatePreAccountingProcessingSettingsRequestBody = {
  branchId: string | null;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
};
export type AccM2StrediskoGetCentresApiResponse =
  | /** status 200 Standard response */ ({
      m2centres?: ({
        name?: string | null;
        code?: string | null;
        validFrom?: string | null;
        validTo?: string | null;
      } | null)[];
    } | null)
  | /** status 204 Standard response */ void;
export type AccM2StrediskoGetCentresApiArg = {
  /** uctovnyDatum */
  accountingDate?: string | null;
  /** ucetPokladne */
  code?: string | null;
};
export type ModuleSourceEnum =
  | 'accounting'
  | 'business-case'
  | 'service-case'
  | 'sale-vehicle'
  | 'service-direct';
export type ServiceSourceEnum = 'omnetic' | 'caris';
export type DocumentSourceEnum = 'internal' | 'external';
export type DocumentSourceRequestBody = {
  documentSource: DocumentSourceEnum;
  moduleSource: ModuleSourceEnum;
  serviceSource: ServiceSourceEnum;
  /** Id of the created document */
  moduleSourceId: string | null;
};
export type DocumentSourceResponseBody = {
  /** Internal (from omnetic) or external (from metada etc.) */
  documentSource: DocumentSourceEnum | null;
  /** Module where the document is created. Ex: Accounting, BusinessCase, ServiceCase etc. */
  moduleSource: ModuleSourceEnum | null;
  /** Service where the document is created. Omnetic or Caris */
  serviceSource: ServiceSourceEnum | null;
  /** Id of the created document */
  moduleSourceId: string | null;
};
export type PaymentInfoRequestBodyV2 = {
  paymentMethod:
    | 'BANK_TRANSFER'
    | 'CARD'
    | 'CASH'
    | 'CASH_ON_DELIVERY'
    | 'OFFSET'
    | 'FREE_OF_CHARGE';
  bankAccount?: string | null;
  bankName?: string | null;
  iban?: string | null;
  swift?: string | null;
  variableSymbol?: string | null;
  constantSymbol?: string | null;
  specificSymbol?: string | null;
};
type VatTypeSummaryResponseBody = {
  groupSummary: PriceResponseBody;
  additionalCalculation: PriceResponseBody | null;
  vatRate: string;
};
export type RoundingDefinitionAndValidityResponseBodyRead = {
  definition: RoundingDefinitionResponseBodyRead;
  validity: ValidityResponseBody;
};
export type TaxDocumentCalculationResponseBodyRead = {
  taxableSupplyDate: string;
  vatTypes: VatType[];
  vatTypesSummary: {
    [key: string]: VatTypeSummaryResponseBody;
  };
  priceWithVat: MoneyResponseBody;
  priceWithoutVat: MoneyResponseBody;
  priceWithVatBeforeRounding: MoneyResponseBody;
  vat: MoneyResponseBody;
  items: TaxDocumentCalculationItemResponseBody[];
  summaryItems: TaxDocumentCalculationSummaryItemResponseBody[];
  usedRoundingDefinitionAndValidity: RoundingDefinitionAndValidityResponseBodyRead | null;
};
export type CorrectiveTaxDocumentItemV2RequestBody = {
  correctedItemId: string;
  description: string;
  quantity: Decimal;
  unit: string;
  pricePerUnit: Decimal;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
  itemId: string;
  type: InvoicingDocumentAccountingItemType;
  relatedItemId: string | null;
  discount?: InvoicingDocumentItemDiscountRequestBody | null;
};
export type InvoiceProformaV4RequestBody = {
  items: InvoicingDocumentItemRequestBody[];
  documentSeriesId: string;
  footerNote?: string | null;
  internalNote?: string | null;
  paid?: boolean;
  markAsSent?: boolean;
  branchId: string;
  issueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency: Alpha3;
  note?: string | null;
  billingInformationId: string;
  customer: CustomerRequestBody;
  dueDate: string;
  paymentInfo: PaymentInfoRequestBody;
  additionalCustomer?: AdditionalCustomerRequestBody | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBody | null;
  documentSource: DocumentSourceRequestBody;
};
export type CreateCashReceiptV4RequestBody = {
  cashRegisterId: string;
  billingInformationId: string;
  type: CashRegisterDocumentTypeEnum;
  kindOfPayment: CashRegisterDocumentKindOfPaymentEnum;
  issuedAt: string;
  /** When true then the document is paid by card. Balance is not updated. CardTransaction is created and paired to the document. */
  paidByCard: boolean;
  variableSymbol?: string | null;
  customerId?: string | null;
  purpose?: string | null;
  note?: string | null;
  items: CreateCashRegisterDocumentItemRequestBody[];
  /** ID of branch for obtaining DocumentLimitDefinition, or null for Tenant */
  branchId: string | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBody;
  relatedInvoicingDocumentId?: string | null;
  relatedInvoicingDocumentType?: InvoicingDocumentTypeUnion | null;
  documentSource: DocumentSourceRequestBody;
};
export type CreateCashReceiptV4ApiResponse = /** status 200  */ UuidResponseBody;
export type CreateCashReceiptV4ApiArg = {
  createCashRegisterDocumentRequestBody: CreateCashReceiptV4RequestBody;
};
export type CreateInvoiceProformaV4ApiResponse = /** status 200  */ UuidResponseBody;
export type CreateInvoiceProformaV4ApiArg = {
  invoiceProformaV4RequestBody: InvoiceProformaV4RequestBody;
};
export type CorrectiveTaxDocumentV4RequestBody = {
  documentSeriesId: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentType;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentInfo: PaymentInfoRequestBodyV2;
  items: CorrectiveTaxDocumentItemV2RequestBody[];
  additionalCustomer?: AdditionalCustomerRequestBody | null;
  customFields?: CustomFieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBody | null;
  invoiceCategory?: string | null;
  documentSource: DocumentSourceRequestBody;
};
export type CreateCorrectiveTaxDocumentV4ApiResponse = /** status 200  */ UuidResponseBody;
export type CreateCorrectiveTaxDocumentV4ApiArg = {
  correctiveTaxDocumentV4RequestBody: CorrectiveTaxDocumentV4RequestBody;
};
export type InvoiceProformaV4ResponseBodyRead = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  paymentState: PaymentStateResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdById: string;
  createdAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  documentSummary: TaxDocumentCalculationResponseBodyRead;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBodyRead | null;
  documentSource: DocumentSourceResponseBody;
};
export type GetInvoiceProformaV4ApiResponse = /** status 200  */ InvoiceProformaV4ResponseBodyRead;
export type GetInvoiceProformaV4ApiArg = {
  invoiceProformaId: string;
};
export type PaymentInfoResponseBodyV2 = {
  paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET' | 'FREE_OF_CHARGE';
  bankAccount: string | null;
  bankName: string | null;
  iban: string | null;
  swift: string | null;
  variableSymbol: string | null;
  constantSymbol: string | null;
  specificSymbol: string | null;
};

export type InvoiceV4ResponseBodyRead = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  paymentState: PaymentStateResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  createdById: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBodyV2;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  documentSummary: TaxDocumentCalculationResponseBodyRead;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBodyRead | null;
  invoiceCategory: string | null;
  documentSource: DocumentSourceResponseBody;
};
export type InvoiceV4ResponseBody = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  paymentState: PaymentStateResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  createdById: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBodyV2;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  documentSummary: TaxDocumentCalculationResponseBody;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: FieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
  invoiceCategory: string | null;
  documentSource: DocumentSourceResponseBody;
};

export type BalanceInvoiceV4ResponseBody = {
  invoice: InvoiceV4ResponseBody;
  proformaIds: string[];
};
export type BalanceInvoiceV4ResponseBodyRead = {
  invoice: InvoiceV4ResponseBodyRead;
  proformaIds: string[];
};
export type GetBalanceInvoiceV4ApiResponse = /** status 200  */ BalanceInvoiceV4ResponseBodyRead;
export type GetBalanceInvoiceV4ApiArg = {
  balanceInvoiceId: string;
};
export type AccountingPaymentMethod = 'UNDEFINED' | 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET';
export type InvoiceV4RequestBody = {
  items: InvoicingDocumentItemRequestBody[];
  documentSeriesId: string;
  footerNote?: string | null;
  internalNote?: string | null;
  paid?: boolean;
  markAsSent?: boolean;
  branchId: string;
  issueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency: Alpha3;
  note?: string | null;
  billingInformationId: string;
  customer: CustomerRequestBody;
  dueDate: string;
  paymentInfo: PaymentInfoRequestBodyV2;
  additionalCustomer?: AdditionalCustomerRequestBody | null;
  customFields?: CustomFieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBody | null;
  invoiceCategory?: string | null;
  documentSource: DocumentSourceRequestBody;
};
export type CashReceiptV4ResponseBodyRead = {
  id: string;
  number: string;
  createdAt: string;
  createdById: string;
  cashRegisterId: string;
  paymentTransactionId: string | null;
  type: CashRegisterDocumentTypeEnum;
  kindOfPayment: CashRegisterDocumentKindOfPaymentEnum;
  state: CashRegisterDocumentStateEnum;
  issuedAt: string;
  isTaxDocument: boolean;
  cancelledDocumentId: string | null;
  relatedInvoicingDocumentNumber: string | null;
  variableSymbol: string | null;
  customerId: string | null;
  purpose: string | null;
  note: string | null;
  cashRegisterBalanceBefore: Money | null;
  cashRegisterBalanceAfter: Money | null;
  totalPriceWithoutVat: Money;
  totalPriceWithVat: Money;
  items: CashRegisterDocumentItemResponseBody[];
  issuer: IssuerResponseBody;
  cashReceiptSummary: TaxDocumentCalculationResponseBodyRead;
  paymentMethod: AccountingPaymentMethod;
  customFields: FieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody;
  exchangeRateRatioCashReceiptSummary: TaxDocumentCalculationResponseBodyRead | null;
  relatedInvoicingDocumentId: string | null;
  relatedInvoicingDocumentType: InvoicingDocumentType | null;
  documentSource: DocumentSourceResponseBody;
};
export type GetCashReceiptV4ApiResponse = /** status 200  */ CashReceiptV4ResponseBodyRead;
export type GetCashReceiptV4ApiArg = {
  cashRegisterDocumentId: string;
};
export type CorrectiveTaxDocumentTypeEnum = 'full-correction' | 'partial-correction';
export type CorrectiveTaxDocumentV5ResponseBodyRead = {
  id: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentType;
  invoicingDocumentNumber: string;
  number: string;
  items: InvoicingDocumentItemResponseBody[];
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  dueSinceIssueDateInDays: number;
  paymentState: PaymentStateResponseBody;
  author: string;
  createdAt: string;
  customer: CustomerResponseBody;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  paymentInfo: PaymentInfoResponseBodyV2;
  supplier: SupplierResponseBody;
  currency: Alpha3;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentPrescriptionId: string;
  branchId: string;
  documentSummary: TaxDocumentCalculationResponseBodyRead;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  additionalCustomer: AdditionalCustomerResponseBody | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBodyRead | null;
  invoiceCategory: string | null;
  documentSource: DocumentSourceResponseBody;
  correctiveTaxDocumentType: CorrectiveTaxDocumentTypeEnum;
};
export type GetCorrectiveTaxDocumentV5ApiResponse = CorrectiveTaxDocumentV5ResponseBodyRead;
export type GetCorrectiveTaxDocumentV5ApiArg = {
  correctiveTaxDocumentId: string;
};

export type CreateInvoiceV4ApiResponse = UuidResponseBody;
export type CreateInvoiceV4ApiArg = {
  invoiceV4RequestBody: InvoiceV4RequestBody;
};

export type GetInvoiceV4ApiResponse = InvoiceV4ResponseBodyRead;
export type GetInvoiceV4ApiArg = {
  invoiceId: string;
};
export type GetBalanceInvoiceByInvoiceProformaIdV4ApiResponse = BalanceInvoiceV4ResponseBodyRead;
export type GetBalanceInvoiceByInvoiceProformaIdV4ApiArg = {
  invoiceProformaId: string;
};
type ValidityResponseBody = {
  /** Date from which the validity is valid */
  validFrom: string;
  roundType: RoundType;
  roundStep: RoundStep;
  /** Will be the rounding item taxed or not? */
  taxRounding: boolean;
};
export type RoundingDefinitionResponseBodyRead = {
  id: string;
  branchId: string | null;
  paymentType: PaymentType;
  validities: ValidityResponseBody[];
  /** Provides information on whether this is a system definition that cannot be deleted. */
  isSystem: boolean;
};
type RoundStep = '0.01' | '0.05' | '0.1' | '0.5' | '1' | '5' | '10' | '100';

export type CreateCorrectiveTaxDocumentSettingApiResponse =
  CorrectiveTaxDocumentSettingResponseBody;
export type CreateCorrectiveTaxDocumentSettingApiArg = {
  createCorrectiveTaxDocumentSettingRequestBody: CreateCorrectiveTaxDocumentSettingRequestBody;
};
export type DeleteCorrectiveTaxDocumentSettingApiResponse = unknown;
export type DeleteCorrectiveTaxDocumentSettingApiArg = {
  id: string;
};
export type GetCorrectiveTaxDocumentSettingApiResponse = CorrectiveTaxDocumentSettingResponseBody;
export type GetCorrectiveTaxDocumentSettingApiArg = {
  id: string;
};
export type UpdateCorrectiveTaxDocumentSettingApiResponse =
  CorrectiveTaxDocumentSettingResponseBody;
export type UpdateCorrectiveTaxDocumentSettingApiArg = {
  id: string;

  updateCorrectiveTaxDocumentSettingRequestBody: UpdateCorrectiveTaxDocumentSettingRequestBody;
};
export type CreateCorrectiveTaxDocumentType = 'without_proforma' | 'with_proforma';
export type CorrectiveTaxDocumentSettingResponseBody = {
  id: string;
  branchId: string | null;
  createCorrectiveTaxDocumentType: CreateCorrectiveTaxDocumentType;
  isSystem: boolean;
};
export type CreateCorrectiveTaxDocumentSettingRequestBody = {
  branchId: string;
  createCorrectiveTaxDocumentType: CreateCorrectiveTaxDocumentType;
};
export type UpdateCorrectiveTaxDocumentSettingRequestBody = {
  branchId: string | null;
  createCorrectiveTaxDocumentType: CreateCorrectiveTaxDocumentType;
};
export type AccPatchDocumentExportDateSetNullApiResponse =
  | ({
      errors?: ({
        message?: string | null;
        code?: string | null;
      } | null)[];
      validationErrors?: (string | null)[];
    } | null)
  | void;
export type AccPatchDocumentExportDateSetNullApiArg = {
  id: string | null;
  type:
    | 'invoice'
    | 'balance-invoice'
    | 'proforma-invoice'
    | 'corrective-tax-document'
    | 'expensive-cash-receipt'
    | 'incoming-cash-receipt'
    | 'tax-document-for-payment';
};
export type TaxDocumentForPaymentV4ResponseBodyRead = {
  id: string;
  invoiceProformaId: string;
  userId: string;
  number: string;
  createdAt: string;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  internalNote: string | null;
  footerNote: string | null;
  items: InvoicingDocumentItemResponseBody[];
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  taxDocumentSummary: TaxDocumentCalculationResponseBodyRead;
  exchangeRateRatio: ExchangeRateRatioBody | null;
  exchangeRateRatioTaxDocumentSummary: TaxDocumentCalculationResponseBodyRead | null;
  documentSource: DocumentSourceResponseBody;
};
export type GetTaxDocumentForPaymentV4ApiResponse = TaxDocumentForPaymentV4ResponseBodyRead;
export type GetTaxDocumentForPaymentV4ApiArg = {
  id: string;
};

export type AccountingCorrectiveTaxDocumentItemsResponseBody = {
  items: InvoicingDocumentItemResponseBody[];
};
export type AccountingGetCorrectiveTaxDocumentItemsApiResponse =
  AccountingCorrectiveTaxDocumentItemsResponseBody;
export type AccountingGetCorrectiveTaxDocumentItemsApiArg = {
  balanceInvoiceId: string;
};
export type GetListCorrectiveTaxDocumentSettingApiResponse =
  CorrectiveTaxDocumentSettingResponseBody[];
export type GetListCorrectiveTaxDocumentSettingApiArg = {};
